import React from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import Form from '../../../Common/Form/Form';
import APIClient from '../../../../../util/APIClient';
import styles from './InviteUser.module.scss';
import FormTextField from '../../../Common/FormTextField/FormTextField';
import FormAlert from '../../../Common/FormAlert/FormAlert';
import FormSelect from '../../../Common/FormSelect/FormSelect';
import useApiData from '../../../../../hooks/useApiData';
import Loading from '../../../Common/Loading/Loading';
import { getErrorComponentFromHttpError } from '../../../Common/Error/Error';

const GROUP_OPTIONS = [
  {
    label: 'Administrators',
    value: 'administrators',
    key: 'administrators',
  },
  {
    label: 'Organisation Non-contributors',
    value: 'organisationNonContributors',
    key: 'organisationNonContributors',
  },
  {
    label: 'Users',
    value: 'users',
    key: 'users',
  },
];

export default function InviteUser() {
  const [formAlert, setFormAlert] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [hasSubmitted, setHasSubmitted] = React.useState(false);
  const { data, isLoading, error } = useApiData({
    path: `/administrator/groups`,
  });

  async function onSubmit(d) {
    setIsSubmitting(true);
    const userData = {
      firstName: d.firstName,
      lastName: d.lastName,
      email: d.email,
      groups: d.groups,
    };
    try {
      await APIClient.post('/administrator/users', userData);
      setHasSubmitted(true);
      setFormAlert({
        severity: 'success',
        title: 'User invited successfully!',
        message:
          'User account has been successfully created. An e-mail will be sent to the new user asking them to complete registration.',
      });
    } catch {
      setFormAlert({
        severity: 'error',
        title: 'Could not invite user!',
        message:
          'Something went wrong during user account creation. Please contact the administrator for further support.',
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoading) {
    return <Loading message="Fetching groups..." />;
  }

  if (error) {
    return getErrorComponentFromHttpError(error);
  }

  return (
    <div className={styles.inviteUser}>
      <p className={styles.description}>
        Use this form to invite a new user to the platform. Doing so will create an account and send an invitational
        e-mail that will require the user to complete their registration. You need to select at least one group the user
        will belong to.
      </p>
      <Form onSubmit={(d) => onSubmit(d)}>
        <div className={styles.horizontalInputs}>
          <FormTextField
            name="firstName"
            label="First name"
            autocomplete="given-name"
            rules={{
              required: { value: true, message: 'First name is required.' },
            }}
          />
          <FormTextField
            name="lastName"
            label="Last name"
            autocomplete="family-name"
            rules={{
              required: { value: true, message: 'Last name is required.' },
            }}
          />
        </div>
        <FormTextField
          name="email"
          label="E-mail address"
          autocomplete="email"
          rules={{
            required: { value: true, message: 'E-mail address is required.' },
            pattern: {
              value: /^\S+@\S+$/i,
              message: 'E-mail address must be a valid e-mail.',
            },
          }}
        />
        <FormSelect
          name="groups"
          label="Groups"
          options={data.map((g) => ({
            label: g.label
              .replace(/([A-Z])/g, ' $1')
              .trim()
              .replace(/^./, (char) => char.toUpperCase())
              .replace(/ (a-z)/, (char) => ` ${char.toUpperCase()}`),
            value: g.label,
            key: g.label,
          }))}
          multiple
          variant="outlined"
          rules={{
            required: {
              value: true,
              message: 'At least one group needs to be selected.',
            },
          }}
        />
        <div className={styles.formControls}>
          <LoadingButton variant="contained" type="submit" loading={isSubmitting} disabled={hasSubmitted} fullWidth>
            <span>Send invitation</span>
          </LoadingButton>
        </div>
        {formAlert && <FormAlert alert={formAlert} />}
      </Form>
    </div>
  );
}
